/**
 * Loumars.Theme — Google Tag Manager (GTM) Initialization Loader Script
 * Strictly standalone per Standalone Assets Policy (zero inline scripts in Liquid/Razor).
 * Resolves Container ID from <meta name="gtm-container-id"> or script[data-gtm-id],
 * initializes window.dataLayer, and asynchronously loads gtm.js.
 */
/* eslint-disable no-console */
export {};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    pushGtmEvent?: (event: string, params?: Record<string, unknown>) => void;
  }
}

(function (): void {
  'use strict';

  if (typeof window === 'undefined') return;

  // 1. Resolve GTM Container ID
  const metaContainer = document.querySelector<HTMLMetaElement>('meta[name="gtm-container-id"]');
  const scriptTag = document.currentScript as HTMLScriptElement | null;
  const containerId =
    metaContainer?.getAttribute('content')?.trim() ||
    scriptTag?.getAttribute('data-gtm-id')?.trim();

  if (!containerId || containerId === '') {
    return;
  }

  // 2. Resolve optional CSP Nonce
  const metaNonce = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]');
  const nonce =
    metaNonce?.getAttribute('content')?.trim() || scriptTag?.getAttribute('nonce')?.trim();

  // 3. Initialize Google Consent Mode V2 before dataLayer push or gtm.js script injection
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]): void {
    window.dataLayer?.push(args);
  }
  if (!window.gtag) {
    window.gtag = gtag;
  }

  // Check stored consent
  let hasStored = false;
  try {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const [key, val] = c.trim().split('=');
      if (key === 'loumars_cookie_consent' && val) {
        const parsed = JSON.parse(decodeURIComponent(val));
        if (typeof parsed.analytics === 'boolean') {
          hasStored = true;
          gtag('consent', 'default', {
            analytics_storage: parsed.analytics ? 'granted' : 'denied',
            ad_storage: parsed.marketing ? 'granted' : 'denied',
            ad_user_data: parsed.marketing ? 'granted' : 'denied',
            ad_personalization: parsed.marketing ? 'granted' : 'denied',
            functionality_storage: 'granted',
            personalization_storage: parsed.marketing ? 'granted' : 'denied',
            security_storage: 'granted',
          });
          break;
        }
      }
    }
  } catch {}

  if (!hasStored) {
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'denied',
      security_storage: 'granted',
    });
  }

  // 4. Initialize dataLayer
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // 4. Attach safe global helper for vanilla theme widgets
  window.pushGtmEvent = function (event: string, params: Record<string, unknown> = {}): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      ...params,
    });

    const isDev =
      location.hostname === 'localhost' ||
      location.hostname === '127.0.0.1' ||
      location.hostname.endsWith('.local');

    if (isDev) {
      console.log(
        `%c[GTM:dataLayer]%c ${event}`,
        'color: #38bdf8; font-weight: bold;',
        'color: inherit;',
        params
      );
    }
  };

  // 5. Inject GTM / GA4 Loader Script asynchronously during idle or user interaction
  let injected = false;
  const userInteractions = ['touchstart', 'scroll', 'pointerdown', 'keydown'] as const;

  const triggerOnInteraction = (): void => {
    injectGtm();
  };

  const injectGtm = (): void => {
    if (injected || document.getElementById('gtm-loader-script')) {
      return;
    }
    injected = true;

    // Clean up interaction triggers
    userInteractions.forEach((evt) => {
      window.removeEventListener(evt, triggerOnInteraction);
    });

    const isGa4 = containerId.startsWith('G-');
    const gtmScript = document.createElement('script');
    gtmScript.id = 'gtm-loader-script';
    gtmScript.async = true;
    gtmScript.src = isGa4
      ? `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(containerId)}`
      : `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    if (nonce) {
      gtmScript.setAttribute('nonce', nonce);
    }

    if (isGa4) {
      gtag('js', new Date());
      gtag('config', containerId);
    }

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(gtmScript, firstScript);
    } else {
      document.head.appendChild(gtmScript);
    }

    const isDev =
      location.hostname === 'localhost' ||
      location.hostname === '127.0.0.1' ||
      location.hostname.endsWith('.local');

    if (isDev) {
      console.log(
        `%c[GTM/GA4]%c Injected container/measurement ID: ${containerId}`,
        'color: #10b981; font-weight: bold;',
        'color: inherit;'
      );
    }
  };

  userInteractions.forEach((evt) => {
    window.addEventListener(evt, triggerOnInteraction, { once: true, passive: true });
  });

  const scheduleDeferredInjection = (): void => {
    if ('requestIdleCallback' in window) {
      (
        window as unknown as {
          requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
        }
      ).requestIdleCallback(injectGtm, { timeout: 6000 });
    } else {
      setTimeout(injectGtm, 5000);
    }
  };

  if (document.readyState === 'complete') {
    scheduleDeferredInjection();
  } else {
    window.addEventListener('load', scheduleDeferredInjection, { once: true });
  }
})();
